import React, { ReactNode, useState } from 'react';
import classnames from 'classnames';
import MaterialModal from '@mui/material/Modal';
import * as css from './confirmationDialog.module.scss';
import { useIntl } from 'react-intl';
import Button from '../button';
import Icon, { IconInterface, IconType } from '../icon';
import Checkbox from '../input/checkbox';

type CustomizableDialogProps<T extends HTMLElement> = React.HTMLAttributes<T>;

type CustomizableDialogDivProps = CustomizableDialogProps<HTMLDivElement>;

type CustomizableDialogContainerProps =
  CustomizableDialogProps<HTMLDivElement> & {
    isOpen: boolean;
    onCancel: () => void;
    modalRef?: React.ForwardedRef<HTMLDivElement>;
    dataCy?: string;
  };

export const CustomizableDialogContainer: React.FC<
  React.PropsWithChildren<CustomizableDialogContainerProps>
> = ({ isOpen, onCancel, modalRef, dataCy, className, children, ...props }) => (
  <MaterialModal
    open={isOpen}
    style={{
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
    }}
    ref={modalRef}
  >
    <div
      className={classnames(css.alpBaseModal, className)}
      role={'dialog'}
      data-cy={dataCy ? dataCy : 'confirmation-dialog'}
      {...props}
    >
      <div className={css.cancelButton}>
        <Button.Close onClick={onCancel} />
      </div>
      {children}
    </div>
  </MaterialModal>
);

export const CustomizableDialogHeader: React.FC<
  React.PropsWithChildren<CustomizableDialogDivProps>
> = ({ className, children, ...props }) => (
  <div className={classnames(css.alpModalHeader, className)} {...props}>
    {children}
  </div>
);

export const CustomizeableDialogIcon: React.FC<
  React.PropsWithChildren<IconInterface>
> = (props) => <Icon className={css.icon} {...props} />;

export const CustomizeableDialogTitlesAndIcon: React.FC<
  React.PropsWithChildren<CustomizableDialogDivProps>
> = ({ children, ...props }) => (
  <div className={css.titlesAndIcon} {...props}>
    {children}
  </div>
);

export const CustomizeableDialogTitles: React.FC<
  React.PropsWithChildren<CustomizableDialogDivProps>
> = ({ children, ...props }) => (
  <div className={css.titles} {...props}>
    {children}
  </div>
);

export const CustomizeableDialogTitle: React.FC<
  React.PropsWithChildren<CustomizableDialogProps<HTMLHeadElement>>
> = ({ children, ...props }) => (
  <h2 className={css.title} {...props}>
    {children}
  </h2>
);

export const CustomizeableDialogSubtitle: React.FC<
  React.PropsWithChildren<CustomizableDialogProps<HTMLParagraphElement>>
> = ({ children, ...props }) => (
  <p className={css.subtitle} {...props}>
    {children}
  </p>
);

export const CustomizableDialogContent: React.FC<
  React.PropsWithChildren<CustomizableDialogDivProps>
> = ({ className, children, ...props }) => (
  <div className={classnames(css.alpModalContents, className)} {...props}>
    {children}
  </div>
);

export const CustomizableDialogFooter: React.FC<
  React.PropsWithChildren<CustomizableDialogDivProps>
> = ({ className, children, ...props }) => (
  <div className={classnames(css.alpModalFooter, className)} {...props}>
    {children}
  </div>
);

export const CustomizableDialogButtonGroup: React.FC<
  React.PropsWithChildren<CustomizableDialogDivProps>
> = ({ children, className, ...props }) => (
  <div className={classnames(css.buttonContainer, className)} {...props}>
    {children}
  </div>
);

export interface ConfirmationDialogProps {
  isOpen: boolean;
  title: ReactNode;
  subtitle?: ReactNode;
  extraClasses?: string[];
  headerClasses?: string[];
  footerClasses?: string[];
  contentCss?: string;
  onClose: (ok: boolean, checked?: boolean) => void;
  checkboxLabel?: string;
  confirmationRequired?: boolean;
  cancelLabel?: string;
  submitLabel?: string;
  submitDisabled?: boolean;
  formId?: string;
  hideCancelBtn?: boolean;
  dataCy?: string;
  modalRef?: React.ForwardedRef<HTMLDivElement>;
  children?: ReactNode;
}

export const ConfirmationDialog = ({
  isOpen,
  onClose,
  children,
  title,
  subtitle,
  extraClasses,
  headerClasses,
  footerClasses,
  checkboxLabel,
  confirmationRequired,
  cancelLabel,
  submitLabel,
  submitDisabled,
  formId,
  hideCancelBtn,
  dataCy,
  contentCss,
  modalRef,
}: ConfirmationDialogProps) => {
  const { formatMessage } = useIntl();
  const cancelButtonText =
    cancelLabel || formatMessage({ id: 'confirmationDialog.cancel' });
  const submitButtonText =
    submitLabel || formatMessage({ id: 'confirmationDialog.ok' });

  const [checked, setChecked] = useState(false);
  const handleCancel = () => onClose(false, checked);
  const handleOk = () => !submitDisabled && onClose(true, checked);
  const toggleChecked = () => {
    setChecked(!checked);
  };

  return (
    <CustomizableDialogContainer
      isOpen={isOpen}
      onCancel={handleCancel}
      modalRef={modalRef}
      dataCy={dataCy}
      className={classnames(extraClasses)}
    >
      <CustomizableDialogHeader className={classnames(headerClasses)}>
        <CustomizeableDialogTitlesAndIcon>
          <CustomizeableDialogIcon type={IconType.Info} />
          <CustomizeableDialogTitles>
            <CustomizeableDialogTitle>{title}</CustomizeableDialogTitle>
            {subtitle && (
              <CustomizeableDialogSubtitle>
                {subtitle}
              </CustomizeableDialogSubtitle>
            )}
          </CustomizeableDialogTitles>
        </CustomizeableDialogTitlesAndIcon>
      </CustomizableDialogHeader>
      <CustomizableDialogContent className={contentCss}>
        {children}
      </CustomizableDialogContent>
      <CustomizableDialogFooter className={classnames(footerClasses)}>
        {checkboxLabel && (
          // TODO Remove native checkbox support
          <Checkbox
            id={'confirmation-dialog-checkbox'}
            label={checkboxLabel}
            checked={checked}
            setChecked={toggleChecked}
          />
        )}
        <CustomizableDialogButtonGroup>
          {!hideCancelBtn && (
            // TODO Remove native hidden cancel btn
            <Button.Quaternary
              onClick={handleCancel}
              className="confirm-cancel"
              data-cy="cancel-button"
            >
              {cancelButtonText}
            </Button.Quaternary>
          )}
          <Button.Secondary
            disabled={submitDisabled || (confirmationRequired && !checked)}
            onClick={handleOk}
            className="confirm-ok"
            data-cy="confirm-button"
            form={formId}
          >
            {submitButtonText}
          </Button.Secondary>
        </CustomizableDialogButtonGroup>
      </CustomizableDialogFooter>
    </CustomizableDialogContainer>
  );
};
